import { debounce, entries, isEmpty, last } from "lodash";
import { Typography } from "PFComponents/typography";
import { hoursToMinutes, minutesToHours } from "PFCore/helpers/date";
import { useBookingUpdate } from "PFCore/hooks/queries";
import { useBookingPartUpdate } from "PFCore/hooks/queries/bookings/part_update/use_booking_part_update";
import { useBookingDelete } from "PFCore/hooks/queries/bookings/use_booking_delete";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { RequirementType } from "PFTypes";
import React, { useMemo } from "react";

import { EditMode } from "../../../../../../../types/edit_mode";
import { useBookingRowsContext } from "../../../../../context/booking_rows_context";
import { useBookingBarContext } from "../../booking_bar_provider";
import { Input } from "./input";
import css from "./item_content.module.scss";
import { useDurationBreakdown } from "./use_duration_breakdown";
import { useRefreshAfterUpdate } from "./use_refresh_after_update";

const MAX_HOURS_IN_ONE_DAY = 24;
const UPDATE_DELAY = 300;

export type ItemContentProps = {
  dates: { date: string; hidden: boolean }[];
};

export const ItemContent = ({ dates }: ItemContentProps) => {
  const {
    booking,
    bar: { row }
  } = useBookingBarContext();
  const { rowsLoadingByKey, wallPlannerEditMode, editMode } = useBookingRowsContext();

  const durationBreakdown = useDurationBreakdown();
  const { utc, formatISODate } = useDateFormatter();

  const { update: updateBooking, isUpdating } = useBookingUpdate(booking?.id);
  const { update: updateBookingPart, isUpdating: isPartUpdating } = useBookingPartUpdate(booking?.id);
  const { delete: deleteBooking, isDeleting } = useBookingDelete(booking?.id);
  const { refresh } = useRefreshAfterUpdate();

  const isDisabled =
    isUpdating > 0 ||
    isPartUpdating > 0 ||
    isDeleting ||
    rowsLoadingByKey[row.key] ||
    wallPlannerEditMode.loading;

  const durationInHours = useMemo(() => {
    const datesList = dates.map(({ date }) => date);
    const durationItems = entries(durationBreakdown).filter(([date]) => datesList.includes(date));
    if (isEmpty(durationItems)) {
      return undefined;
    }
    return minutesToHours(durationItems.reduce((sum, [, value]) => sum + value, 0));
  }, [durationBreakdown, dates]);

  const handleChangeDebounced = debounce(async (value: number | undefined) => {
    if (!booking || value === undefined) {
      return;
    }
    const startDate = dates[0];
    const requirementValue = hoursToMinutes(Number(value));
    const isWholeBookingUpdated =
      startDate?.date === formatISODate(utc(booking.start_date)) &&
      last(dates)?.date === formatISODate(utc(booking.end_date));
    if (isWholeBookingUpdated) {
      const shouldDelete = value <= 0;
      if (shouldDelete) {
        await deleteBooking({});
      } else {
        await updateBooking({
          requirementType: RequirementType.TotalHours,
          requirementValue: requirementValue,
          overridesCalendar: true
        });
      }
    } else {
      const endDate = formatISODate(utc(last(dates)?.date).add(1, "day"));
      await updateBookingPart({
        requirementType: RequirementType.TotalHours,
        requirementValue: requirementValue,
        overridesCalendar: true,
        startDate: startDate?.date,
        endDate: endDate
      });
    }
    refresh();
    wallPlannerEditMode.setLoading(false);
  }, UPDATE_DELAY);

  return (
    <div className={css.content}>
      {editMode === EditMode.WallPlanner ? (
        <Input
          defaultValue={durationInHours}
          onChange={handleChangeDebounced}
          min={0}
          max={MAX_HOURS_IN_ONE_DAY * dates.length}
          disabled={isDisabled}
        />
      ) : (
        <Typography variant="bodyBold" noMargin clipOverflow>
          {durationInHours ?? ""}
        </Typography>
      )}
    </div>
  );
};
